import { useState, useEffect, useMemo, useTransition } from 'react'
import { Link } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { Bookmark, Loader2, Trash2, Search, X, SlidersHorizontal, AlertTriangle } from 'lucide-react'
import { motion } from 'framer-motion'
import { getViewMode, isAuthenticated } from '@/lib/utils'
import { toast } from 'sonner'

export function BookmarksPage() {
  const [viewMode, setViewMode] = useState(getViewMode())
  const authenticated = isAuthenticated()
  const [isPending, startTransition] = useTransition()
  const queryClient = useQueryClient()
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false)
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSource, setSelectedSource] = useState('all')
  const [dateRange, setDateRange] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [showFilters, setShowFilters] = useState(false)

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['bookmarks'],
    queryFn: ({ pageParam = 1 }) => newsApi.getBookmarks(pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      
      // Extract page number from URL
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    enabled: authenticated,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })

  const bookmarks = useMemo(() => {
    return data?.pages.flatMap(page => page.data.results) || []
  }, [data?.pages])

  // Get unique sources from bookmarked articles
  const uniqueSources = useMemo(() => {
    const sourcesMap = new Map()
    bookmarks.forEach(article => {
      if (article.feeder) {
        sourcesMap.set(article.feeder.alias, {
          alias: article.feeder.alias,
          name: article.feeder.display_name || article.feeder.name,
        })
      }
    })
    return Array.from(sourcesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [bookmarks])

  // Client-side filtering
  const filteredBookmarks = useMemo(() => {
    let filtered = [...bookmarks]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(article =>
        article.title?.toLowerCase().includes(search) ||
        article.description?.toLowerCase().includes(search) ||
        article.feeder?.display_name?.toLowerCase().includes(search) ||
        article.feeder?.name?.toLowerCase().includes(search)
      )
    }

    // Source filter
    if (selectedSource !== 'all') {
      filtered = filtered.filter(article => article.feeder?.alias === selectedSource)
    }

    // Date range filter (based on when bookmark was created)
    if (dateRange !== 'all') {
      const now = new Date()
      const filterDate = new Date()
      
      if (dateRange === 'today') {
        filterDate.setHours(0, 0, 0, 0)
      } else if (dateRange === 'week') {
        filterDate.setDate(now.getDate() - 7)
      } else if (dateRange === 'month') {
        filterDate.setMonth(now.getMonth() - 1)
      }

      filtered = filtered.filter(article => {
        // Use bookmark creation date if available, otherwise fall back to article date
        const bookmarkDate = article.bookmark?.created_at || article.created_at || article.published_at
        const articleDate = new Date(bookmarkDate)
        return articleDate >= filterDate
      })
    }

    // Sort (by bookmark creation date)
    if (sortBy === 'newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.bookmark?.created_at || a.created_at || a.published_at)
        const dateB = new Date(b.bookmark?.created_at || b.created_at || b.published_at)
        return dateB - dateA
      })
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.bookmark?.created_at || a.created_at || a.published_at)
        const dateB = new Date(b.bookmark?.created_at || b.created_at || b.published_at)
        return dateA - dateB
      })
    }

    return filtered
  }, [bookmarks, searchTerm, selectedSource, dateRange, sortBy])

  const hasActiveFilters = searchTerm || selectedSource !== 'all' || dateRange !== 'all' || sortBy !== 'newest'

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedSource('all')
    setDateRange('all')
    setSortBy('newest')
  }

  // Clear all bookmarks
  const handleClearAll = async () => {
    setClearAllConfirmOpen(false)
    
    try {
      // Remove bookmarks one by one
      const promises = bookmarks.map(article => newsApi.toggleBookmark(article.slug))
      await Promise.all(promises)
      
      // Invalidate and refetch
      queryClient.invalidateQueries(['bookmarks'])
      toast.success('All bookmarks cleared')
    } catch (error) {
      console.error('Failed to clear bookmarks:', error)
      toast.error('Failed to clear bookmarks. Please try again.')
    }
  }

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Not authenticated
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-slate-50 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <Bookmark className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign In to View Bookmarks</h1>
          <p className="text-gray-600 mb-6">
            Save articles you want to read later and access them from any device.
          </p>
          <Link to="/login">
            <Button size="lg">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="h-10 w-48 bg-gray-200 rounded animate-shimmer mb-8" />
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <NewsCardSkeleton key={i} variant={viewMode} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-red-50 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Failed to Load Bookmarks</h1>
          <p className="text-gray-600 mb-6">Something went wrong. Please try again.</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onViewModeChange={setViewMode} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
                  <Bookmark className="w-8 h-8 text-primary dark:text-blue-400" />
                  My Bookmarks
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  {filteredBookmarks.length > 0 
                    ? `${filteredBookmarks.length} article${filteredBookmarks.length !== 1 ? 's' : ''} ${hasActiveFilters ? 'found' : ''}`
                    : bookmarks.length > 0 ? 'No articles match your filters' : 'No bookmarks yet'
                  }
                </p>
              </div>
              
              {bookmarks.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setClearAllConfirmOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              )}
            </div>
          </motion.div>

          {/* Filters */}
          {bookmarks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search bookmarks..."
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Filter Toggles */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Date Range */}
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>

                {/* Source Filter */}
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={uniqueSources.length === 0}
                >
                  <option value="all">All Sources ({uniqueSources.length})</option>
                  {uniqueSources.map((source) => (
                    <option key={source.alias} value={source.alias}>
                      {source.name}
                    </option>
                  ))}
                </select>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="ml-auto"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* Empty state */}
          {bookmarks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-white rounded-xl shadow-sm"
            >
              <div className="bg-gray-100 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <Bookmark className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Bookmarks Yet</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Start saving articles you want to read later. Click the bookmark icon on any article to save it here.
              </p>
              <Link to="/">
                <Button>
                  Explore Articles
                </Button>
              </Link>
            </motion.div>
          ) : filteredBookmarks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm"
            >
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <Search className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Results Found</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                No bookmarks match your current filters. Try adjusting your search or filters.
              </p>
              <Button onClick={clearFilters}>
                Clear Filters
              </Button>
            </motion.div>
          ) : (
            <>
              {/* Bookmarked articles */}
              <div className="space-y-4">
                {filteredBookmarks.map((article, index) => (
                  <NewsCard
                    key={article.alias || article.id}
                    article={article}
                    variant={viewMode}
                    index={index}
                    showDelete={true}
                    onDelete={async (article) => {
                      try {
                        await newsApi.toggleBookmark(article.slug)
                        // Invalidate and refetch
                        queryClient.invalidateQueries(['bookmarks'])
                        toast.success('Bookmark removed')
                      } catch (error) {
                        toast.error('Failed to remove bookmark')
                      }
                    }}
                  />
                ))}
                
                {/* Loading next page skeletons */}
                {(isFetchingNextPage || isPending) && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <NewsCardSkeleton key={`skeleton-${i}`} variant={viewMode} />
                    ))}
                  </>
                )}
              </div>

              {/* Load more trigger */}
              <div ref={loadMoreRef} className="py-4">
                {!hasNextPage && bookmarks.length > 0 && (
                  <p className="text-sm text-gray-500 text-center">You've reached the end</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Clear All Confirmation Modal */}
        {clearAllConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setClearAllConfirmOpen(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Clear All Bookmarks?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Are you sure you want to remove all <span className="font-medium">{bookmarks.length} bookmarks</span>? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setClearAllConfirmOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleClearAll}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
